import React, { type FC, memo, useCallback } from "react";
import { Alert, Platform } from "react-native";
import { Controller, type FieldValues, type Control, type Path } from "react-hook-form";
import DateTimePicker from "react-native-modal-datetime-picker";

import {
  usePickerField,
  PickerInput,
  WebPickerInput,
  formatIsoDate,
  formatDateForHtmlInput,
  htmlDateToIso,
  DATE_CONSTRAINTS,
  PICKER_CONSTANTS,
  type DateControllerProps,
} from "../Pickers";

type DateFieldProps = {
  value?: string;
  onChange: (value: string) => void;
  error?: string;
  defaultDate?: string;
  size?: "small" | "medium" | "large";
};

type RenderParams = {
  field: {
    onChange: (value: string) => void;
    value: string;
  };
};

const dateFormatter = (value: string | undefined, defaultDate?: string): string => {
  if (value) return formatIsoDate(value);
  if (defaultDate) return formatIsoDate(defaultDate);
  return "";
};

const DateField: FC<DateFieldProps> = memo(({ value, onChange, error, defaultDate, size = "medium" }) => {
  const { isVisible, show, hide, paddingBottom } = usePickerField({
    minimumDate: DATE_CONSTRAINTS.MIN_DATE,
    maximumDate: DATE_CONSTRAINTS.MAX_DATE,
  });

  const handleConfirm = useCallback(
    (date: Date) => {
      if (date > DATE_CONSTRAINTS.MAX_DATE || date < DATE_CONSTRAINTS.MIN_DATE) {
        Alert.alert(
          "Invalid Date",
          `Please select a date between ${formatIsoDate(DATE_CONSTRAINTS.MIN_DATE.toISOString())} and ${formatIsoDate(DATE_CONSTRAINTS.MAX_DATE.toISOString())}.`,
        );
      } else {
        const formattedDate = date.toISOString();
        onChange(formattedDate);
        hide();
      }
    },
    [onChange, hide],
  );

  return (
    <>
      <PickerInput
        value={value}
        formatter={(v) => dateFormatter(v, defaultDate)}
        onPress={show}
        icon="calendar"
        error={error}
        size={size}
      />
      <DateTimePicker
        isVisible={isVisible}
        mode="date"
        onConfirm={handleConfirm}
        onCancel={hide}
        date={new Date(defaultDate ?? "")}
        maximumDate={DATE_CONSTRAINTS.MAX_DATE}
        minimumDate={DATE_CONSTRAINTS.MIN_DATE}
        pickerComponentStyleIOS={{ height: PICKER_CONSTANTS.IOS_PICKER_HEIGHT }}
        modalStyleIOS={{ paddingBottom }}
      />
    </>
  );
});

const DateFieldWeb: FC<DateFieldProps> = memo(({ value, onChange, error, defaultDate, size = "medium" }) => {
  const inputValue =
    value ? formatDateForHtmlInput(value)
    : defaultDate ? formatDateForHtmlInput(defaultDate)
    : "";

  const handleChange = useCallback(
    (dateValue: string) => {
      onChange(htmlDateToIso(dateValue));
    },
    [onChange],
  );

  return (
    <WebPickerInput
      type="date"
      value={inputValue}
      onChange={handleChange}
      error={error}
      size={size}
      min="2000-01-01"
      max="2030-01-01"
    />
  );
});

export const DateController = <T extends FieldValues>({
  control,
  error,
  name,
  defaultDate,
  size = "medium",
}: DateControllerProps<T>) => {
  const render = useCallback(
    ({ field: { onChange, value } }: RenderParams) => {
      if (Platform.OS === "web") {
        return <DateFieldWeb value={value} onChange={onChange} error={error} defaultDate={defaultDate} size={size} />;
      }
      return <DateField value={value} onChange={onChange} error={error} defaultDate={defaultDate} size={size} />;
    },
    [error, defaultDate, size],
  );

  return <Controller control={control} name={name} render={render} />;
};
